import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useRouter } from 'shared-routing';
import { DiningReservationTemplate } from 'voyager';
import type { CalendarView, DiningReservationRestaurant } from 'voyager';

import { BasketDrawerVersion } from '@common/components/BasketDrawer';
import { Routes } from '@common/constants';
import { useGetRestaurantConfig } from '@features/tables/hooks/useGetRestaurantConfig';
import { useGetTableDepositValue } from '@features/tables/hooks/useGetTableDepositValue';
import { DEFAULT_MAX_PARTY_SIZE } from '@features/tables/constants';
import {
  bookingPath,
  findBookingRestaurant,
  restaurantBookingSegment,
} from '@features/tables/bookingRoute';
import { getDiningCovers } from '@features/tables/diningParty';
import { useTranslations } from '@lang';
import { yourJourneySelectors } from '@store/yourJourney';
import type { ParsedJourneyParams } from '@store/yourJourney/selectors';
import { getWeekMonthStart } from '@utils/date';
import rewriteHttp from '@utils/rewriteHttp';

import type { DiningReservationV2Props } from './DiningReservationV2.types';
import { useDiningPreferences } from './useDiningPreferences';
import { useReservationBasketId } from '@common/hooks';
import { formatIsoDate, useDiningRestaurants, useRestaurantWeekAvailability } from './queries';
import type { DiningAvailabilityTimeslot } from './schemas';
import { isBookableSlot } from './timeslots';

function isoDaysFromToday(days = 0) {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return formatIsoDate(date);
}

function withRestaurantParam(url: string, segment: string) {
  const [pathname, search = ''] = url.split('?');
  const params = new URLSearchParams(search);
  params.set('restaurant', segment);
  return `${pathname}?${params.toString()}`;
}

function replaceRestaurantInUrl(segment: string) {
  const as = `${bookingPath(segment)}${window.location.search}`;
  const state = window.history.state;

  window.history.replaceState(
    state && typeof state === 'object'
      ? {
          ...state,
          ...(typeof state.url === 'string'
            ? { url: withRestaurantParam(state.url, segment) }
            : {}),
          ...(typeof state.as === 'string' ? { as } : {}),
        }
      : state,
    '',
    as
  );
}

export const DiningReservationV2: FC<DiningReservationV2Props> = ({
  restaurantId,
  property,
  basketDetails,
  language,
  createBasket,
  updateBasket,
  fetchBasketDetails,
}) => {
  const { t } = useTranslations();
  const router = useRouter();
  const journey = useSelector(yourJourneySelectors.getParsedJourneyParams) as ParsedJourneyParams;
  const rawFromDate = useSelector(yourJourneySelectors.getActualFromDateValue);
  const searchedDate =
    rawFromDate && journey.fromDate?.isValid() ? journey.fromDate.format('YYYY-MM-DD') : undefined;
  const { basketId, basketNotReady } = useReservationBasketId({
    basketDetails,
    propertyId: property.id,
    language,
    fetchBasketDetails,
  });

  const { data: restaurantsData } = useDiningRestaurants();
  const restaurants = Array.isArray(restaurantsData) ? restaurantsData : [];

  const [selectedRestaurantId, setSelectedRestaurantId] = useState<string | undefined>(
    restaurantId
  );
  const [guestCount, setGuestCount] = useState(
    Math.min(getDiningCovers(journey), DEFAULT_MAX_PARTY_SIZE)
  );
  const [activeDate, setActiveDate] = useState(() => searchedDate ?? isoDaysFromToday());
  const [visibleMonth, setVisibleMonth] = useState<string | undefined>(undefined);
  const [selectedDate, setSelectedDate] = useState<string | undefined>(searchedDate);
  const [selectedTimeslot, setSelectedTimeslot] = useState<string | undefined>(undefined);
  const [selectedPromotionId, setSelectedPromotionId] = useState<string | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isBasketDrawerOpen, setIsBasketDrawerOpen] = useState(false);
  const submittingRef = useRef(false);

  const propertyModules = Object.keys(property.modulesExcludingDisabled || {});

  const selectedRestaurant = findBookingRestaurant(restaurants, selectedRestaurantId);

  const {
    availability,
    timeslots,
    promotionsByDate,
    isLoading,
    refetch: refetchAvailability,
  } = useRestaurantWeekAvailability(selectedRestaurant, activeDate, guestCount, 0);
  const dayPromotions = (selectedDate && promotionsByDate[selectedDate]) || [];

  const restaurantName = selectedRestaurant?.restaurantName ?? '';
  const { data: restaurantConfig } = useGetRestaurantConfig(
    property.id,
    activeDate,
    restaurantName,
    Boolean(restaurantName)
  );
  const { data: todayRestaurantConfig } = useGetRestaurantConfig(
    property.id,
    isoDaysFromToday(),
    restaurantName,
    Boolean(restaurantName)
  );

  const slotMax =
    timeslots.reduce(
      (max, slot) => (slot.maxPartySize != null ? Math.max(max, slot.maxPartySize) : max),
      0
    ) || null;
  const slotMinRaw = timeslots.reduce(
    (min, slot) => (slot.minPartySize != null ? Math.min(min, slot.minPartySize) : min),
    Infinity
  );
  const slotMin = Number.isFinite(slotMinRaw) ? slotMinRaw : null;
  const maxPartySize = slotMax ?? restaurantConfig?.maxOnlinePartySize ?? DEFAULT_MAX_PARTY_SIZE;
  const minPartySize = slotMin ?? restaurantConfig?.minOnlinePartySize ?? 1;
  const maxGuests = Math.max(1, maxPartySize);
  const minGuests = Math.max(1, minPartySize);
  const bookingWindowDays = Number(todayRestaurantConfig?.acceptBookingsDaysInAdvance);
  const maxDate = bookingWindowDays > 0 ? isoDaysFromToday(bookingWindowDays) : undefined;
  const calendarView: CalendarView = visibleMonth ? 'month' : 'week';

  useEffect(() => {
    if (guestCount > maxGuests) {
      setGuestCount(maxGuests);
    } else if (guestCount < minGuests) {
      setGuestCount(minGuests);
    }
  }, [maxGuests, minGuests]);

  const daySlots = useMemo(() => {
    const slotsByStartTime = new Map<string, DiningAvailabilityTimeslot>();
    timeslots
      .filter((slot) => slot.date === selectedDate && isBookableSlot(slot, dayPromotions))
      .forEach((slot) => {
        const existing = slotsByStartTime.get(slot.startTime);
        if (!existing || (!existing.hasStandardAvailability && slot.hasStandardAvailability)) {
          slotsByStartTime.set(slot.startTime, slot);
        }
      });
    return [...slotsByStartTime.values()];
  }, [timeslots, selectedDate, dayPromotions]);

  const dayTimeslots = daySlots.map((slot) => ({
    value: slot.startTime,
    label: slot.startTime,
  }));

  const selectedSlot = daySlots.find((slot) => slot.startTime === selectedTimeslot);

  const restaurantOptions: DiningReservationRestaurant[] = restaurants.map((restaurant) => ({
    id: restaurant.providerId,
    name: restaurant.name,
    subtitle: restaurant.subtitle ?? undefined,
    imageUrl: restaurant.imageUrls[0] ? rewriteHttp(restaurant.imageUrls[0]) : undefined,
  }));

  const formatMoney = (majorUnits: number, currency: string) =>
    new Intl.NumberFormat(language, {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(majorUnits);

  const menuCurrency: string | undefined = restaurantConfig?.currencyCode;
  const menuOptions = (selectedSlot?.availablePromotions ?? []).flatMap((available) => {
    const promotion = dayPromotions.find((entry) => entry.id === available.id);
    if (!promotion) return [];
    const secondaryLabel =
      available.basePrice && available.basePrice > 0 && menuCurrency
        ? formatMoney(available.basePrice, menuCurrency)
        : undefined;
    return [{ value: promotion.id, label: promotion.name, secondaryLabel }];
  });

  const selectedPromotion = selectedSlot?.availablePromotions.find(
    (available) => available.id === selectedPromotionId
  );
  const menuRequired = Boolean(selectedSlot) && !selectedSlot?.hasStandardAvailability;

  const { data: deposit, isInitialLoading: depositLoading } = useGetTableDepositValue(
    property.uuid,
    guestCount,
    selectedSlot ? selectedSlot.date : '',
    selectedSlot && selectedRestaurant ? String(selectedRestaurant.restaurantId) : '',
    selectedPromotion?.id
  );
  const depositAwaitsMenu = menuOptions.length > 0 && !selectedPromotion;
  const depositMessage =
    deposit && deposit.cost.amount > 0 && !depositAwaitsMenu
      ? t('dining.depositMessage', {
          amount: formatMoney(deposit.cost.amount / 100, deposit.cost.currency),
        })
      : undefined;

  const leaveTime = selectedSlot?.isLeaveTimeRequired
    ? selectedPromotion?.leaveTime ?? selectedSlot.leaveTime
    : null;
  const leaveTimeNote = leaveTime ? t('tables.returnInfo', { time: leaveTime }) : undefined;

  const heroSource = selectedRestaurant?.imageUrls[0] ?? restaurants[0]?.imageUrls[0];
  const heroImage = heroSource ? rewriteHttp(heroSource) : undefined;

  const clearTimeslot = () => {
    setSelectedTimeslot(undefined);
    setSelectedPromotionId(undefined);
  };

  const selectTimeslot = (time: string) => {
    setSelectedTimeslot(time);
    setSelectedPromotionId(undefined);
  };

  useEffect(() => {
    if (isLoading) return;
    if (selectedTimeslot && !selectedSlot) clearTimeslot();
    else if (selectedPromotionId && !selectedPromotion) setSelectedPromotionId(undefined);
  }, [isLoading, selectedTimeslot, selectedSlot, selectedPromotionId, selectedPromotion]);

  const selectRestaurant = (id: string) => {
    const picked = restaurants.find((restaurant) => restaurant.providerId === id);
    setSelectedRestaurantId(id);
    clearTimeslot();
    if (picked && picked !== selectedRestaurant) {
      replaceRestaurantInUrl(restaurantBookingSegment(picked));
    }
  };

  const preferences = useDiningPreferences();

  const handleReserve = async () => {
    if (
      !selectedRestaurant ||
      !selectedSlot ||
      (menuRequired && !selectedPromotion) ||
      submittingRef.current ||
      basketNotReady ||
      depositLoading
    )
      return;

    const tableItem = {
      type: 'tables',
      adults: guestCount,
      children: 0,
      note: preferences.values.note,
      dietaryRequirements: preferences.values.dietaryRequirements,
      celebrations: preferences.values.celebrations,
      bookable: {
        ids: {
          timeslot: selectedSlot.timeSlot,
          restaurant: selectedRestaurant.restaurantName,
          restaurantName: selectedRestaurant.configName,
          restaurantConfig: selectedRestaurant.restaurantId,
          promotionId: selectedPromotion?.id,
          timeslotReservationId:
            selectedPromotion?.timeslotReservationId ??
            selectedSlot.timeslotReservationId ??
            undefined,
        },
      },
    };

    submittingRef.current = true;
    setIsSubmitting(true);
    try {
      if (basketId) {
        const existingItems = Array.isArray(basketDetails?.items) ? basketDetails.items : [];
        await updateBasket(
          property.id,
          basketId,
          language,
          { items: [tableItem, ...existingItems] },
          false,
          true
        );
      } else {
        await createBasket(property.id, language, { items: [tableItem] }, true);
      }

      setIsBasketDrawerOpen(true);
    } catch {
      refetchAvailability();
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <DiningReservationTemplate
        preferenceCategories={preferences.preferenceCategories}
        onPreferenceOptionToggle={preferences.onPreferenceOptionToggle}
        onPreferenceOtherChange={preferences.onPreferenceOtherChange}
        onPreferenceFreeTextChange={preferences.onPreferenceFreeTextChange}
        imageUrl={heroImage}
        imageAlt={selectedRestaurant?.name ?? t('app.dining')}
        onBack={() => router.push({ pathname: Routes.TABLES_HOME })}
        restaurants={restaurantOptions}
        selectedRestaurantId={selectedRestaurant?.providerId ?? selectedRestaurantId}
        onSelectRestaurant={selectRestaurant}
        guestCount={guestCount}
        minGuests={minGuests}
        maxGuests={maxGuests}
        onGuestCountChange={(count) => {
          setGuestCount(count);
          clearTimeslot();
        }}
        activeDate={visibleMonth ?? activeDate}
        selectedDate={selectedDate}
        calendarView={calendarView}
        availability={availability}
        maxDate={maxDate}
        onDateSelect={(date) => {
          setSelectedDate(date);
          clearTimeslot();
          setActiveDate(date);
          setVisibleMonth(undefined);
        }}
        onWeekNavigate={(date) => {
          if (visibleMonth) {
            setVisibleMonth(date);
            return;
          }
          setActiveDate(date);
          setSelectedDate(undefined);
          clearTimeslot();
        }}
        onCalendarViewChange={(view) =>
          setVisibleMonth(view === 'month' ? getWeekMonthStart(activeDate) : undefined)
        }
        timeslots={dayTimeslots}
        isLoading={isLoading}
        selectedTimeslot={selectedTimeslot}
        onTimeslotSelect={selectTimeslot}
        progressStep={selectedTimeslot ? 2 : 1}
        menuOptions={menuOptions}
        selectedMenuId={selectedPromotionId}
        onMenuSelect={setSelectedPromotionId}
        onMenuClear={() => setSelectedPromotionId(undefined)}
        menuRequired={menuRequired}
        leaveTimeNote={leaveTimeNote}
        depositMessage={depositMessage}
        onReserve={handleReserve}
        reserveDisabled={isSubmitting || basketNotReady || depositLoading}
        labels={{
          heading: t('dining.reservationHeading'),
          subheading: t('dining.reservationSubheading'),
          restaurantTitle: t('dining.restaurantTitle'),
          restaurantSubtitle: t('dining.restaurantSubtitle'),
          reservationTitle: t('dining.reservationTitle'),
          reservationSubtitle: t('dining.reservationSubtitle'),
          guestLabel: t('dining.guests'),
          timeLabel: t('dining.time'),
          noTimeslotsLabel: t('dining.noTimeslots'),
          menuTitle: t('dining.menuTitle'),
          menuFieldLabel: t('dining.menuFieldLabel'),
          menuClearLabel: t('dining.noMenu'),
          menuRequiredLabel: t('app.requiredField'),
          changeRestaurant: t('dining.changeRestaurant'),
          dismiss: t('dining.close'),
          reserveLabel: t('dining.reserve'),
          back: t('dining.back'),
          noImage: t('basket.noImageLabel'),
          calendar: {
            previousWeek: t('spa.booking.calendar.previousWeek'),
            nextWeek: t('spa.booking.calendar.nextWeek'),
            previousMonth: t('aria.previousMonth'),
            nextMonth: t('aria.nextMonth'),
            openCalendar: t('spa.booking.calendar.openCalendar'),
            closeCalendar: t('spa.booking.calendar.closeCalendar'),
          },
          preferences: {
            title: t('dining.preferencesTitle'),
            otherButtonLabel: t('dining.preferencesOther'),
          },
        }}
      />
      <BasketDrawerVersion
        open={isBasketDrawerOpen}
        onClose={() => setIsBasketDrawerOpen(false)}
        propertyModules={propertyModules}
      />
    </>
  );
};
